import AppBar from '@mui/material/AppBar';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import { default as Grid } from '@mui/material/Grid2';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';

import useInvoicePdf from 'hooks-ts/useInvoicePdf';
import { useEffect, useState } from 'react';
import handleError from 'utils-ts/handle-error';
import { type Invoice } from '@a1/shared';

interface InvoicePdfDialogProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setOpenInvoiceSend: React.Dispatch<React.SetStateAction<boolean>>;
  invoice?: Invoice | null;
  liveInvoice?: Invoice | null;
}

const InvoicePdfDialog = ({ open, setOpen, invoice, setOpenInvoiceSend, liveInvoice }: InvoicePdfDialogProps) => {
  const { getInvoicePdfData } = useInvoicePdf();
  const invoiceId = invoice?.id;

  const [invoicePdfUrl, setInvoicePdfUrl] = useState<string | null>(null);
  const [loadingInvoicePdfData, setLoadingInvoicePdfData] = useState(true);

  const handleClose = () => {
    setOpen(false);
  };

  const handleSend = () => {
    setOpenInvoiceSend(true);
  };

  useEffect(() => {
    const getPdfData = async () => {
      try {
        setLoadingInvoicePdfData(true);
        const data = await getInvoicePdfData({ invoiceId, invoiceData: liveInvoice });
        if (data.url) {
          setInvoicePdfUrl(data.url);
        }
      } catch (error) {
        handleError(error);
      } finally {
        setLoadingInvoicePdfData(false);
      }
    };

    if (open && invoiceId) {
      getPdfData();
    } else {
      setLoadingInvoicePdfData(false);
    }
  }, [getInvoicePdfData, invoiceId, liveInvoice, open]);

  return (
    <>
      <Dialog open={open} onClose={handleClose} fullScreen>
        <AppBar sx={{ position: 'relative' }}>
          <Toolbar>
            <IconButton edge="start" color="inherit" onClick={handleClose} aria-label="close">
              <CloseIcon />
            </IconButton>
            <Typography sx={{ ml: 2, flex: 1, color: 'white' }} component="div">
              {invoice?.invoiceNumber}
            </Typography>
            <Button color="secondary" onClick={handleSend} aria-label="Send invoice by email">
              Send to
            </Button>
          </Toolbar>
        </AppBar>
        {loadingInvoicePdfData ? (
          <LinearProgress />
        ) : (
          <>
            {invoice ? (
              <DialogContent>
                <Grid container>
                  {invoicePdfUrl && (
                    <Grid size={12} height="calc(100vh - 150px)">
                      <iframe title="invoice-pdf" src={invoicePdfUrl} width="100%" height="100%" />
                    </Grid>
                  )}
                </Grid>
              </DialogContent>
            ) : (
              <DialogContent>Invoice could not be loaded.</DialogContent>
            )}
          </>
        )}
      </Dialog>
    </>
  );
};

export default InvoicePdfDialog;
